import React, { useState } from "react";
import { Link } from "react-router-dom";

/**
 * On-page contact form for Alaine's Bench. Replaces the old mailto: links,
 * which silently did nothing for visitors without a configured mail app.
 * Submissions are stored server-side (POST /api/inquiries, source
 * "alaines-bench") so no lead or support request is ever lost.
 */
export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    website: "", // honeypot — hidden from real visitors
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      const res = await fetch("/api/inquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: "alaines-bench" }),
      });
      if (!res.ok && res.status !== 204) {
        const body = await res.json().catch(() => null);
        setError(
          (body && body.error) ||
            "Something went wrong sending your message. Please email support@alainesbench.com directly.",
        );
        return;
      }
      setSubmitted(true);
    } catch {
      setError(
        "Something went wrong sending your message. Please email support@alainesbench.com directly.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-white/60 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="contact-home-link">
            <div className="h-8 w-8 rounded-md bg-emerald-800 text-white grid place-items-center font-bold">A</div>
            <div className="leading-none">
              <div className="font-bold text-lg tracking-tight">
                Alaine's Bench<sup className="text-[0.5em] font-semibold ml-0.5 align-super">™</sup>
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-0.5">
                Contact
              </div>
            </div>
          </Link>
          <Link to="/" className="text-sm text-stone-500 hover:text-emerald-800">
            &larr; Back
          </Link>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-3xl font-bold tracking-tight text-stone-900 mb-2">Get in touch</h1>
        <p className="text-stone-600 mb-8">
          Questions, support, or feedback — send a message and we'll get back to you at the
          email you provide.
        </p>

        <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8">
          {submitted ? (
            <div className="text-center py-10" data-testid="contact-success">
              <h2 className="text-xl font-bold text-stone-900 mb-2">Message received!</h2>
              <p className="text-stone-600">
                Thanks for reaching out — we'll follow up soon. Prefer email? You can always
                reach us at{" "}
                <a href="mailto:support@alainesbench.com" className="text-emerald-800 underline">
                  support@alainesbench.com
                </a>
                .
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-1.5">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange}
                  data-testid="contact-name"
                  className="w-full border border-stone-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-700/40 focus:border-emerald-700"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-1.5">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  data-testid="contact-email"
                  className="w-full border border-stone-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-700/40 focus:border-emerald-700"
                />
              </div>
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-stone-700 mb-1.5">
                  Company <span className="text-stone-400">(optional)</span>
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  value={form.company}
                  onChange={handleChange}
                  data-testid="contact-company"
                  className="w-full border border-stone-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-700/40 focus:border-emerald-700"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-1.5">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  maxLength={2000}
                  value={form.message}
                  onChange={handleChange}
                  data-testid="contact-message"
                  className="w-full border border-stone-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-700/40 focus:border-emerald-700 resize-none"
                />
              </div>

              <input
                type="text"
                name="website"
                value={form.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />

              {error && (
                <p className="text-sm text-red-600" data-testid="contact-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="w-full bg-emerald-800 hover:bg-emerald-900 disabled:opacity-60 text-white font-medium rounded-lg py-3 transition-colors"
              >
                {sending ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
